import { forwardRef } from 'react';
import { MdInfo, MdPerson } from 'react-icons/md';
import { CustomLabels } from '@services/tenantsService';

interface UnmatchedUser {
  name: string;
  role: string;
}

interface UnmatchedUsersProps {
  users: UnmatchedUser[];
  type: 'mentor' | 'mentee';
  getLabel: (key: keyof CustomLabels, defaultValue: string) => string;
}

const UnmatchedUsers = forwardRef<HTMLDivElement, UnmatchedUsersProps>(
  ({ users, type, getLabel }, ref) => {
    if (users.length === 0) return null;

    const label = type === 'mentor' ? getLabel('mentor', 'Mentor') : getLabel('mentee', 'Mentee');
    const otherLabel = type === 'mentor' ? getLabel('mentee', 'mentee') : getLabel('mentor', 'mentor');

    return (
      <div ref={ref} className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <MdInfo className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="font-medium text-gray-800 dark:text-gray-200">
            Unmatched {label}s
          </h3>
        </div>
        <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
          {users.length} {label.toLowerCase()}s could not be matched with available {otherLabel.toLowerCase()}s{type === 'mentee' ? ' due to compatibility thresholds' : ''}.
        </p>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {users.slice(0, 10).map((user, index) => (
            <div key={index} className="flex items-center justify-between bg-white dark:bg-navy-800 rounded p-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-100 dark:bg-gray-900/30 rounded-full flex items-center justify-center">
                  <MdPerson className="h-2 w-2 text-gray-600 dark:text-gray-400" />
                </div>
                <span className="font-medium text-navy-700 dark:text-white">
                  {user.name}
                </span>
              </div>
              <span className="text-gray-500 dark:text-gray-400">
                {user.role}
              </span>
            </div>
          ))}
          {users.length > 10 && (
            <div className="text-center py-2">
              <span className="text-gray-600 dark:text-gray-400 text-xs">
                +{users.length - 10} more unmatched {label.toLowerCase()}s
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

UnmatchedUsers.displayName = 'UnmatchedUsers';

export default UnmatchedUsers;
